import { z } from 'zod';
/**
 * Shared API contract pieces (SRS 0.3 default conventions):
 * standard error envelope, cursor pagination, common primitives.
 */
export const ERROR_CODES = {
    VALIDATION_FAILED: 'validation_failed',
    UNAUTHENTICATED: 'unauthenticated',
    FORBIDDEN: 'forbidden',
    TENANT_CONTEXT_MISSING: 'tenant_context_missing',
    /**
     * A valid token, presented against a hospital it does not belong to. Kept
     * apart from TENANT_CONTEXT_MISSING so that a boundary being enforced is
     * legible in the log as exactly that, rather than reading like a caller who
     * forgot a header — which is what a stale browser tab produces.
     */
    TENANT_MISMATCH: 'tenant_mismatch',
    TENANT_SUSPENDED: 'tenant_suspended',
    MODULE_NOT_ENABLED: 'module_not_enabled',
    NOT_FOUND: 'not_found',
    CONFLICT: 'conflict',
    IDEMPOTENCY_CONFLICT: 'idempotency_conflict',
    RATE_LIMITED: 'rate_limited',
    PAYLOAD_TOO_LARGE: 'payload_too_large',
    INTERNAL: 'internal_error',
    /**
     * 423: this install is read-only (docs/SYNC.md) — a local server that has
     * not checked in within its grace period, was copied to another machine or
     * runs a version the owner retired; or the cloud mirror of a hospital that
     * runs locally. Upper-case like ACCESS_BLOCKED because the web client keys
     * on the status, not the code.
     */
    INSTALL_LOCKED: 'INSTALL_LOCKED',
    /**
     * 403: the owner has not switched on "Allow offline mode" for this hospital
     * (docs/OFFLINE-MODE.md step 0), so no activation token, enrolment, download
     * PIN or package download. Upper-case for the same reason as INSTALL_LOCKED.
     */
    OFFLINE_NOT_ENABLED: 'OFFLINE_NOT_ENABLED',
};
export const cursorPaginationSchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
export const uuidSchema = z.string().uuid();
/** Loose phone validation — tenants span countries; strict rules live in tenant config. */
export const phoneSchema = z
    .string()
    .trim()
    .min(7)
    .max(20)
    .regex(/^[+0-9][0-9\-\s()]*$/, 'Invalid phone number')
    .refine((value) => {
    const digits = value.replace(/\D/g, '').length;
    return digits >= 7 && digits <= 15;
}, 'Phone number must have 7 to 15 digits');
export const CALENDAR_YEAR_MIN = 1850;
export const CALENDAR_YEAR_MAX = 2200;
export function isRealCalendarDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (year < CALENDAR_YEAR_MIN || year > CALENDAR_YEAR_MAX)
        return false;
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
    .refine(isRealCalendarDate, 'Not a real calendar date');
export const OLDEST_LIVING_YEARS = 130;
export const birthDateSchema = isoDateSchema.refine((value) => {
    const born = Date.parse(`${value}T00:00:00.000Z`);
    if (Number.isNaN(born))
        return false;
    const now = Date.now();
    const oldest = now - OLDEST_LIVING_YEARS * 365.25 * 24 * 60 * 60 * 1000;
    return born <= now && born >= oldest;
}, `A date of birth cannot be in the future or more than ${OLDEST_LIVING_YEARS} years ago`);
export const dueDateSchema = z.string().datetime().refine((value) => {
    const at = Date.parse(value);
    if (Number.isNaN(at))
        return false;
    const year = 365.25 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    return at >= now - year && at <= now + 5 * year;
}, 'A due date must fall between a year ago and five years from now');
/**
 * An optional field as a form submits it.
 *
 * An untouched text input arrives as "" and an untouched numeric one as NaN;
 * neither is a value the person entered, so both are read as "not given"
 * instead of being run through the field's own rules (and failing, e.g.
 * "String must contain at least 7 character(s)" on an empty alternate phone).
 */
export const optionalInput = (schema) => z.preprocess((value) => (typeof value === 'string' && value.trim() === '') || (typeof value === 'number' && Number.isNaN(value))
    ? undefined
    : value, schema.optional());
/** Money at the API boundary is always an integer of minor units. */
export const moneyMinorSchema = z.number().int().min(-10_000_000_000).max(10_000_000_000);
export const HEADER_TENANT = 'x-tenant-id';
export const HEADER_IDEMPOTENCY = 'idempotency-key';
export const HEADER_REQUEST_ID = 'x-request-id';
export const HEADER_BREAK_GLASS_REASON = 'x-break-glass-reason';
/**
 * Branch the caller is currently working in.
 *
 * Optional: without it a user sees every branch their roles reach. With it the
 * server narrows to that one branch — and rejects it outright if the branch is
 * outside the caller's scope, so the header can widen nothing.
 */
export const HEADER_BRANCH = 'x-branch-id';
//# sourceMappingURL=api.js.map